import { useGetMarketplaceOffer } from "@packages/api-client/hooks/marketplaceController";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
	Boxes,
	Building2,
	Calendar,
	Check,
	ChevronRight,
	DollarSign,
	Heart,
	MessageCircle,
	Scale,
	Share2,
	ShieldCheck,
	Tag,
	Users,
} from "lucide-react";
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { useDateFormatter } from "@/hooks/useDateFormatter";
import { cn } from "@/utils/utils";
import { CowPrintArt } from "./-components/CowPrintArt";
import { useFavorites } from "./-components/useFavorites";

export const Route = createFileRoute(
	"/_auth/app/_app/org/$orgId/marketplace/$offerId",
)({
	component: OfferDetailPage,
});

function isFresh(publishedAt?: string): boolean {
	if (!publishedAt) return false;
	return (Date.now() - new Date(publishedAt).getTime()) / 86_400_000 <= 7;
}

function OfferDetailPage() {
	const { orgId, offerId } = Route.useParams();
	const { t } = useTranslation();
	const { formatDateOnly } = useDateFormatter();
	const { data: offer, isLoading } = useGetMarketplaceOffer(offerId);
	const { favorites, toggle } = useFavorites();
	const descriptionRef = useRef<HTMLElement>(null);

	if (isLoading) {
		return (
			<div className="mx-auto w-full max-w-7xl p-4 lg:p-6">
				<div className="grid gap-6 lg:grid-cols-3">
					<div className="space-y-4 lg:col-span-2">
						<div className="h-80 animate-pulse rounded-xl bg-base-200" />
						<div className="h-6 w-2/3 animate-pulse rounded bg-base-200" />
						<div className="h-24 animate-pulse rounded-xl bg-base-200" />
					</div>
					<div className="h-64 animate-pulse rounded-xl bg-base-200" />
				</div>
			</div>
		);
	}

	if (!offer) {
		return (
			<div className="mx-auto w-full max-w-7xl p-6">
				<p className="text-base-content/60">
					{t("marketplace.offer.notFound")}
				</p>
				<Button asChild variant="outline" size="sm" className="mt-4">
					<Link to="/app/org/$orgId/marketplace" params={{ orgId }}>
						{t("marketplace.offer.back")}
					</Link>
				</Button>
			</div>
		);
	}

	const favorited = favorites.has(offer.id);
	const pricingLabel = t(
		`livestock.sales.pricingModels.${offer.pricingModel}`,
		offer.pricingModel,
	);
	const bigPrice =
		offer.askingTotal != null
			? `${offer.currency} ${offer.askingTotal.toLocaleString()}`
			: `${offer.currency} ${offer.unitPrice.toLocaleString()}`;
	const priceSub =
		offer.askingTotal != null
			? `${offer.currency} ${offer.unitPrice.toLocaleString()} / ${pricingLabel}`
			: `/ ${pricingLabel}`;

	const share = () => {
		const url = window.location.href;
		if (navigator.share) {
			navigator.share({ title: offer.title, url }).catch(() => {});
		} else {
			navigator.clipboard?.writeText(url).catch(() => {});
		}
	};

	const scrollToContact = () => {
		descriptionRef.current?.scrollIntoView({
			behavior: "smooth",
			block: "start",
		});
	};

	return (
		<div className="mx-auto w-full max-w-7xl pb-24 lg:pb-0">
			{/* Breadcrumb + actions */}
			<div className="flex items-center justify-between gap-3 border-b border-base-300 px-4 py-3 sm:px-6">
				<nav className="flex min-w-0 items-center gap-1.5 text-sm text-base-content/60">
					<Link
						to="/app/org/$orgId/marketplace"
						params={{ orgId }}
						className="shrink-0 hover:text-primary"
					>
						{t("marketplace.browse.title")}
					</Link>
					<ChevronRight className="size-3.5 shrink-0" />
					<span className="truncate text-base-content">{offer.title}</span>
				</nav>
				<div className="flex shrink-0 items-center gap-2">
					<button
						type="button"
						onClick={() => toggle(offer.id)}
						aria-label={t("marketplace.browse.save")}
						aria-pressed={favorited}
						className={cn(
							"grid size-9 place-items-center rounded-full border border-base-300 transition-colors hover:bg-base-200",
							favorited ? "text-error" : "text-base-content/60",
						)}
					>
						<Heart className={cn("size-4", favorited && "fill-current")} />
					</button>
					<button
						type="button"
						onClick={share}
						aria-label={t("marketplace.detail.share")}
						className="grid size-9 place-items-center rounded-full border border-base-300 text-base-content/60 transition-colors hover:bg-base-200"
					>
						<Share2 className="size-4" />
					</button>
				</div>
			</div>

			<div className="grid gap-6 px-4 py-6 sm:px-6 lg:grid-cols-3">
				{/* ---------------------------------------------------------------- */}
				{/* Left column                                                       */}
				{/* ---------------------------------------------------------------- */}
				<div className="space-y-6 lg:col-span-2">
					{/* Hero mosaic — real photos when present, generated art otherwise */}
					<div className="grid h-72 grid-cols-4 grid-rows-2 gap-2 sm:h-96">
						<div className="relative col-span-4 row-span-2 overflow-hidden rounded-xl sm:col-span-3">
							{offer.images?.[0] ? (
								<img
									src={offer.images[0].url}
									alt={offer.title}
									className="size-full object-cover"
								/>
							) : (
								<CowPrintArt seed={offer.id} spots={6} className="size-full" />
							)}
							<span className="absolute left-4 top-4 rounded-full bg-base-100/90 px-3 py-1 text-sm font-semibold text-base-content shadow-sm backdrop-blur">
								{t(`marketplace.kind.${offer.offerKind}`, offer.offerKind)}
							</span>
							{isFresh(offer.publishedAt) && (
								<span className="absolute right-4 top-4 rounded-md bg-success px-2.5 py-1 text-xs font-semibold text-success-content shadow">
									{t("marketplace.browse.newBadge")}
								</span>
							)}
							<span className="absolute bottom-4 left-4 rounded-md bg-stone-900/70 px-3 py-1.5 text-sm font-medium text-white">
								{t("marketplace.headCount", { n: offer.headCount })}
							</span>
						</div>
						<div className="relative hidden overflow-hidden rounded-xl sm:block">
							{offer.images?.[1] ? (
								<img
									src={offer.images[1].url}
									alt={offer.title}
									className="size-full object-cover"
								/>
							) : (
								<CowPrintArt seed={`${offer.id}-a`} className="size-full" />
							)}
						</div>
						<div className="relative hidden overflow-hidden rounded-xl sm:block">
							{offer.images?.[2] ? (
								<img
									src={offer.images[2].url}
									alt={offer.title}
									className="size-full object-cover"
								/>
							) : (
								<CowPrintArt seed={`${offer.id}-b`} className="size-full" />
							)}
						</div>
					</div>

					{/* Title + price (price repeats here for mobile, hidden on lg) */}
					<div>
						<h1 className="text-2xl font-bold tracking-tight sm:text-3xl">
							{offer.title}
						</h1>
						<p className="mt-2 text-2xl font-bold text-primary lg:hidden">
							{bigPrice}
							<span className="ml-2 align-middle text-sm font-normal text-base-content/60">
								{priceSub}
							</span>
						</p>
					</div>

					{/* About / description — holds the seller's contact details */}
					<section
						ref={descriptionRef}
						className="scroll-mt-20 rounded-xl border border-base-300 bg-base-100 p-5 shadow-sm"
					>
						<h2 className="mb-2 font-semibold">
							{t("marketplace.detail.about")}
						</h2>
						<p
							className={cn(
								"whitespace-pre-line text-sm leading-relaxed",
								offer.description
									? "text-base-content/80"
									: "italic text-base-content/50",
							)}
						>
							{offer.description || t("marketplace.detail.noDescription")}
						</p>
					</section>

					{/* Details attribute grid */}
					<section className="rounded-xl border border-base-300 bg-base-100 p-5 shadow-sm">
						<h2 className="mb-4 font-semibold">
							{t("marketplace.detail.details")}
						</h2>
						<div className="grid grid-cols-2 gap-x-6 gap-y-5 sm:grid-cols-3">
							<Attr
								icon={Boxes}
								label={t("marketplace.detail.attrType")}
								value={t(
									`marketplace.kind.${offer.offerKind}`,
									offer.offerKind,
								)}
							/>
							<Attr
								icon={Users}
								label={t("marketplace.detail.attrHeadCount")}
								value={String(offer.headCount)}
							/>
							<Attr
								icon={DollarSign}
								label={t("marketplace.detail.attrPricing")}
								value={pricingLabel}
							/>
							<Attr
								icon={Tag}
								label={t("marketplace.detail.attrUnitPrice")}
								value={`${offer.currency} ${offer.unitPrice.toLocaleString()}`}
							/>
							<Attr
								icon={Scale}
								label={t("marketplace.detail.attrTotalWeight")}
								value={
									offer.totalWeightKg != null
										? t("marketplace.detail.weightUnit", {
												n: offer.totalWeightKg.toLocaleString(),
											})
										: "—"
								}
							/>
							{offer.publishedAt && (
								<Attr
									icon={Calendar}
									label={t("marketplace.detail.attrPublished")}
									value={formatDateOnly(new Date(offer.publishedAt).getTime())}
								/>
							)}
						</div>
					</section>

					{/* Animals */}
					<section className="rounded-xl border border-base-300 bg-base-100 p-5 shadow-sm">
						<h2 className="mb-4 font-semibold">
							{t("marketplace.offer.animals", { n: offer.items.length })}
						</h2>
						<div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
							{offer.items.map((item) => (
								<div
									key={item.snapshotTagId}
									className="flex items-center justify-between gap-2 rounded-lg border border-base-300 bg-base-200/40 px-3 py-2 text-sm"
								>
									<span className="flex min-w-0 items-center gap-2">
										<span className="grid size-7 shrink-0 place-items-center rounded-full bg-primary/10 font-mono text-[10px] font-semibold text-primary">
											#
										</span>
										<span className="truncate">
											<span className="font-medium">{item.snapshotTagId}</span>
											{item.snapshotName ? (
												<span className="text-base-content/60">
													{" "}
													· {item.snapshotName}
												</span>
											) : null}
										</span>
									</span>
									{item.snapshotWeightKg != null && (
										<span className="shrink-0 whitespace-nowrap text-xs text-base-content/60">
											{t("marketplace.detail.weightUnit", {
												n: item.snapshotWeightKg,
											})}
										</span>
									)}
								</div>
							))}
						</div>
					</section>
				</div>

				{/* ---------------------------------------------------------------- */}
				{/* Right sidebar                                                     */}
				{/* ---------------------------------------------------------------- */}
				<aside className="space-y-4 lg:sticky lg:top-20 lg:self-start">
					{/* Price + CTA card */}
					<div className="rounded-xl border border-base-300 bg-base-100 p-5 shadow-sm">
						<p className="hidden text-3xl font-bold leading-none text-primary lg:block">
							{bigPrice}
						</p>
						<p className="mt-1 hidden text-sm text-base-content/60 lg:block">
							{priceSub}
						</p>

						<Button
							className="mt-4 w-full rounded-full"
							onClick={scrollToContact}
						>
							<MessageCircle className="size-4" />
							{t("marketplace.detail.contactSeller")}
						</Button>
						<p className="mt-2 text-center text-xs text-base-content/60">
							{t("marketplace.detail.contactHint")}
						</p>
					</div>

					{/* Seller card */}
					<div className="rounded-xl border border-base-300 bg-base-100 p-5 shadow-sm">
						<div className="flex items-center gap-3">
							<div className="grid size-11 shrink-0 place-items-center rounded-full bg-primary/10 text-primary">
								<Building2 className="size-5" />
							</div>
							<div className="min-w-0">
								<p className="truncate font-semibold">{offer.sellerOrgName}</p>
								<p className="flex items-center gap-1 text-xs text-base-content/60">
									<ShieldCheck className="size-3.5 text-success" />
									{t("marketplace.detail.verifiedSeller")}
								</p>
							</div>
						</div>
						<p className="mt-3 text-xs text-base-content/60">
							{t("marketplace.detail.memberInfo")}
						</p>

						<div className="mt-4 space-y-2 border-t border-base-300 pt-4">
							<p className="text-sm font-semibold">
								{t("marketplace.detail.whyTitle")}
							</p>
							{[
								t("marketplace.detail.whyTracked"),
								t("marketplace.detail.whySnapshot"),
								t("marketplace.detail.whyTransfer"),
							].map((line) => (
								<p
									key={line}
									className="flex items-start gap-2 text-sm text-base-content/70"
								>
									<Check className="mt-0.5 size-4 shrink-0 text-success" />
									{line}
								</p>
							))}
						</div>
					</div>

					{/* Safety tips */}
					<div className="rounded-xl border border-warning/30 bg-warning/10 p-5">
						<h3 className="mb-1 flex items-center gap-2 font-semibold">
							<ShieldCheck className="size-4 text-warning" />
							{t("marketplace.detail.safetyTitle")}
						</h3>
						<p className="text-sm text-base-content/70">
							{t("marketplace.detail.safetyBody")}
						</p>
					</div>
				</aside>
			</div>

			{/* Mobile sticky action bar */}
			<div className="fixed inset-x-0 bottom-0 z-30 flex items-center justify-between gap-3 border-t border-base-300 bg-base-100/95 p-3 backdrop-blur lg:hidden">
				<div className="min-w-0">
					<p className="truncate text-lg font-bold leading-none text-primary">
						{bigPrice}
					</p>
					<p className="truncate text-xs text-base-content/60">{priceSub}</p>
				</div>
				<Button className="rounded-full" onClick={scrollToContact}>
					<MessageCircle className="size-4" />
					{t("marketplace.detail.contactSeller")}
				</Button>
			</div>
		</div>
	);
}

function Attr({
	icon: Icon,
	label,
	value,
}: {
	icon: typeof Boxes;
	label: string;
	value: string;
}) {
	return (
		<div className="flex items-start gap-2.5">
			<Icon className="mt-0.5 size-5 shrink-0 text-base-content/40" />
			<div className="min-w-0">
				<p className="text-xs text-base-content/50">{label}</p>
				<p className="font-medium">{value}</p>
			</div>
		</div>
	);
}
